/**
 * Token Refresh Logic
 * Handles automatic token refresh for both SSO and Direct Auth
 */

interface RefreshTokenResponse {
  access_token: string
  id_token: string
  expires_in: number
  token_type: string
}

export class TokenRefresh {
  private static isRefreshing = false
  private static refreshPromise: Promise<boolean> | null = null
  private static refreshAttempts = 0
  private static readonly MAX_REFRESH_ATTEMPTS = 3
  private static readonly REFRESH_TIMEOUT = 10000 // 10 seconds

  /**
   * Check if token is expired or about to expire (within 5 minutes)
   */
  static isTokenExpired(): boolean {
    const expiryTime = localStorage.getItem("token_expiry")
    const accessToken = localStorage.getItem("access_token")

    // If no token, consider expired
    if (!accessToken || !expiryTime) {
      return true
    }

    const expiry = parseInt(expiryTime, 10)
    const now = Date.now()
    const fiveMinutes = 5 * 60 * 1000

    // Calculate time until expiry
    const timeUntilExpiry = expiry - now

    // If token has more than 55 minutes left, it's definitely not expired
    // This prevents refresh attempts on fresh tokens (3600s - 300s = 3300s = 55 min)
    if (timeUntilExpiry > 55 * 60 * 1000) {
      return false // Token is fresh
    }

    // Consider expired if within 5 minutes of expiry or already expired
    const expired = timeUntilExpiry <= fiveMinutes

    return expired
  }

  /**
   * Refresh tokens using refresh token
   * Returns true if successful, false otherwise
   */
  static async refreshTokens(): Promise<boolean> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    this.isRefreshing = true
    this.refreshPromise = this.performRefresh()

    try {
      const result = await this.refreshPromise
      return result
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  /**
   * Perform the actual token refresh with timeout and retry limit
   */
  private static async performRefresh(): Promise<boolean> {
    // Check if we've exceeded max attempts
    if (this.refreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
      this.refreshAttempts = 0 // Reset for next login session
      return false
    }

    this.refreshAttempts++

    const refreshToken = localStorage.getItem("refresh_token")
    if (!refreshToken) {
      this.refreshAttempts = 0 // Reset counter
      return false
    }

    const domain = process.env.NEXT_PUBLIC_COGNITO_DOMAIN
    const clientId = process.env.NEXT_PUBLIC_CLIENT_ID

    if (!domain || !clientId) {
      this.refreshAttempts = 0 // Reset counter
      return false
    }

    try {
      // Create abort controller for timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.REFRESH_TIMEOUT
      )

      const response = await fetch(`${domain}/oauth2/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          client_id: clientId,
          refresh_token: refreshToken,
        }),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        return false
      }

      const tokens: RefreshTokenResponse = await response.json()

      // Update tokens in localStorage
      localStorage.setItem("access_token", tokens.access_token)
      localStorage.setItem("id_token", tokens.id_token)

      // Update expiry time
      const expiryTime = Date.now() + (tokens.expires_in || 3600) * 1000
      localStorage.setItem("token_expiry", expiryTime.toString())

      // Note: Refresh token is typically not returned, so we keep the existing one

      this.refreshAttempts = 0 // Reset counter on success
      return true
    } catch {
      return false
    }
  }

  /**
   * Ensure tokens are valid before making API calls
   * Automatically refreshes if needed
   */
  static async ensureValidToken(): Promise<boolean> {
    const isExpired = this.isTokenExpired()

    if (!isExpired) {
      return true // Token is still valid
    }

    const refreshed = await this.refreshTokens()

    if (!refreshed) {
      // Clear all tokens
      this.clearTokens()
      return false
    }

    return true
  }

  /**
   * Clear all tokens from localStorage
   */
  static clearTokens(): void {
    localStorage.removeItem("access_token")
    localStorage.removeItem("id_token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("token_expiry")
    localStorage.removeItem("user_info")
    this.refreshAttempts = 0 // Reset attempt counter
  }
}
